import { useEffect, useState } from 'react';

import { calendarZone, dayRange, localDay } from '../../shared/calendar';
import type { FocusSession } from '../../shared/focus';
import type { LocalRecord, Task, WorkspaceSettingsValue } from '../../shared/model';

import { workspace } from '../lib/engine';
import { Select } from './ui';
import { SETTINGS_ID } from './WorkspaceSettings';

export function DailyLoad({
  records,
  priorities,
}: {
  records: LocalRecord[];
  priorities: LocalRecord[];
}) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);

    return () => clearInterval(timer);
  }, []);

  const day = localDay(new Date(now)),
    range = dayRange(day),
    start = Date.parse(range.startsAt),
    end = Date.parse(range.endsAt);

  const worked = new Set(
    records
      .filter(
        r =>
          r.kind === 'focus' &&
          (r.value as FocusSession).segments.some(
            s => s.correctedMinutes !== 0 && s.startedAt < end && (s.endedAt ?? now) > start,
          ),
      )
      .map(r => (r.value as FocusSession).taskId),
  ).size;

  const known = priorities.reduce((sum, r) => sum + ((r.value as Task).estimateMinutes ?? 0), 0),
    unknown = priorities.filter(r => !(r.value as Task).estimateMinutes).length;

  const settings = records.find(r => r.kind === 'settings'),
    value = settings?.value as WorkspaceSettingsValue | undefined;

  // There is no capacity cap: the count and load are shown instead of work
  // being silently dropped, in the phone's words.
  const open = priorities.length;

  const load: [string, string] =
    open === 0
      ? ['success', 'Nothing left to do today.']
      : open <= 3
        ? ['success', 'A manageable day.']
        : open <= 5
          ? ['attention', 'A full day. Consider moving something.']
          : ['danger', 'More than a day holds. Something will carry over.'];

  return (
    <section className="today-section">
      <div className="section-heading today-section-heading">
        <h2>Day load</h2>
      </div>
      <div
        className={`daily-load tone-${load[0]}`}
        data-load={value?.dailyChecks?.[day] ?? 'unset'}
      >
        <p className="daily-load-words">{load[1]}</p>
        <p className="small-muted">
          {known
            ? `About ${Math.floor(known / 60)}h ${known % 60}m estimated.`
            : 'No estimates yet.'}
          {unknown ? ` ${unknown} without an estimate.` : ''}{' '}
          {worked === 1 ? 'One task' : `${worked} tasks`} with recorded time today.
        </p>
        <label>
          How manageable does today feel?
          <Select
            aria-label="Daily manageability"
            value={value?.dailyChecks?.[day] ?? ''}
            onChange={e => {
              const current = workspace.snapshot().records.find(r => r.kind === 'settings'),
                stored = current?.value as WorkspaceSettingsValue | undefined;

              const dailyChecks = { ...stored?.dailyChecks };

              if (e.target.value) {
                dailyChecks[day] = e.target.value as 'manageable' | 'mixed' | 'overloaded';
              } else {
                delete dailyChecks[day];
              }

              void workspace
                .save(current?.id ?? SETTINGS_ID, 'settings', {
                  ...stored,
                  timezone: stored?.timezone ?? calendarZone(),
                  dailyChecks,
                })
                .catch(() => {});
            }}
          >
            <option value="">Optional check-in</option>
            <option value="manageable">Manageable</option>
            <option value="mixed">Mixed</option>
            <option value="overloaded">Too much</option>
          </Select>
        </label>
      </div>
    </section>
  );
}
